import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  CurrencyDollar,
  ArrowLeft,
  ArrowRight,
  Code,
  Users,
  Rocket,
  CheckCircle,
  Warning,
  Lock,
  Clock,
  ArrowsClockwise,
  ChartLineUp,
  EnvelopeSimple,
} from '@phosphor-icons/react';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const TOTAL_SLIDES = 10;

const ACQUISITIONS = [
  { company: 'SGNL', acquirer: 'CrowdStrike', value: '$740M', year: '2026' },
  { company: 'Protect AI', acquirer: 'Palo Alto Networks', value: '$500-700M', year: '2025' },
  { company: 'Prompt Security', acquirer: 'SentinelOne', value: '~$250M', year: '2025' },
  { company: 'Lakera', acquirer: 'Check Point', value: '~$187M', year: '2025' },
  { company: 'Adaptive Shield', acquirer: 'CrowdStrike', value: 'Undisclosed', year: '2025' },
  { company: 'Robust Intelligence', acquirer: 'Cisco', value: '~$400M', year: '2024' },
];

/* ------------------------------------------------------------------ */
/*  Slide transition variants                                          */
/* ------------------------------------------------------------------ */

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

/* ------------------------------------------------------------------ */
/*  Shared components                                                  */
/* ------------------------------------------------------------------ */

function LogoIcon({ size = 48 }: { size?: number }) {
  return (
    <img src="/logo.png" alt="Palaryn" width={size} height={size} />
  );
}

function SlideContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-8 py-12 sm:px-16 md:px-24">
      {children}
    </div>
  );
}

function SectionTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-6 inline-block rounded-full border border-[var(--border)] bg-[var(--surface-2)] px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-text-tertiary">
      {children}
    </span>
  );
}

function GradientHeading({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h2 className={`text-gradient text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl ${className}`}>
      {children}
    </h2>
  );
}

/* ------------------------------------------------------------------ */
/*  Timeline component (for Slides 2 + 5)                              */
/* ------------------------------------------------------------------ */

interface TimelineEvent {
  time: string;
  text: string;
  severity?: 'normal' | 'warning' | 'danger' | 'success';
}

function Timeline({ events, label }: { events: TimelineEvent[]; label?: string }) {
  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="w-full max-w-2xl">
      {label && (
        <div className="mb-4 text-[10px] font-mono uppercase tracking-wider text-text-tertiary">{label}</div>
      )}
      <div className="relative border-l-2 border-[var(--border)] pl-6">
        {events.map((ev, i) => {
          const colors = {
            normal: 'border-[var(--border)] bg-[var(--surface-1)]',
            warning: 'border-[var(--color-warning-muted)] bg-[rgba(234,179,8,0.04)]',
            danger: 'border-[var(--color-error-muted)] bg-[rgba(239,68,68,0.04)]',
            success: 'border-[var(--color-teal-muted)] bg-[rgba(6,182,164,0.04)]',
          };
          const dotColors = {
            normal: 'bg-[var(--text-tertiary)]',
            warning: 'bg-[var(--color-warning)]',
            danger: 'bg-[var(--color-error)]',
            success: 'bg-accent-teal',
          };
          const sev = ev.severity ?? 'normal';
          return (
            <motion.div key={i} variants={fadeUp} className="relative mb-3 last:mb-0">
              <span className={`absolute -left-[31px] top-3 h-2.5 w-2.5 rounded-full ${dotColors[sev]}`} />
              <div className={`rounded-lg border ${colors[sev]} px-4 py-2.5`}>
                <span className="mr-3 font-mono text-xs text-text-tertiary">{ev.time}</span>
                <span className="text-sm text-text-secondary">{ev.text}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Flywheel component (for Slide 8)                                   */
/* ------------------------------------------------------------------ */

function Flywheel({ steps }: { steps: { label: string; detail: string }[] }) {
  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="w-full max-w-xl">
      {steps.map((step, i) => (
        <motion.div key={i} variants={fadeUp} className="relative flex items-start gap-4 pb-6 last:pb-0">
          {/* Connector line */}
          {i < steps.length - 1 && (
            <div className="absolute left-[15px] top-[32px] h-[calc(100%-20px)] w-px bg-[var(--border)]" />
          )}
          {/* Step number */}
          <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal-muted)] text-xs font-bold text-accent-teal">
            {i + 1}
          </div>
          <div className="pt-1">
            <div className="text-sm font-semibold text-text-primary">{step.label}</div>
            <div className="mt-0.5 text-xs text-text-tertiary">{step.detail}</div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Milestone bar (for Slide 9)                                        */
/* ------------------------------------------------------------------ */

function MilestoneBar({ phases }: { phases: { label: string; items: string[] }[] }) {
  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="w-full max-w-4xl mx-auto">
      <div className="grid gap-4 sm:grid-cols-3">
        {phases.map((phase, i) => (
          <motion.div key={i} variants={fadeUp} className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5">
            <div className="text-xs font-mono font-bold text-accent-teal uppercase tracking-wider">{phase.label}</div>
            <div className="mt-3 space-y-2">
              {phase.items.map((item, j) => (
                <div key={j} className="flex items-start gap-2 text-xs text-text-secondary">
                  <ChartLineUp size={12} weight="bold" className="mt-0.5 shrink-0 text-accent-teal" />
                  {item}
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Individual Slides                                                  */
/* ------------------------------------------------------------------ */

/* Slide 1: Cover */
function SlideCover() {
  return (
    <SlideContainer>
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }} className="mb-8">
        <LogoIcon size={72} />
      </motion.div>
      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }} className="text-5xl font-bold tracking-tight text-text-primary sm:text-6xl md:text-7xl text-center">
        Palaryn
      </motion.h1>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }} className="mt-4 text-xl text-text-secondary sm:text-2xl text-center max-w-2xl font-medium">
        Before you scale AI agents, install brakes.
      </motion.p>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5 }} className="mt-3 text-sm text-text-tertiary text-center max-w-xl">
        Cost control and security for every AI agent tool call
      </motion.p>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.5 }} className="mt-8">
        <span className="rounded-full border border-[var(--border)] bg-[var(--surface-2)] px-5 py-2 text-sm font-mono text-text-tertiary">
          Seed Round | March 2026
        </span>
      </motion.div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.5 }} className="mt-4 text-xs text-text-tertiary text-center">
        Stripe billing live &nbsp;|&nbsp; MCP + CLI + SDKs &nbsp;|&nbsp; Product live
      </motion.p>
    </SlideContainer>
  );
}

/* Slide 2: The Horror Scenario */
function SlideHorror() {
  return (
    <SlideContainer>
      <SectionTag>The Problem</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        Last Tuesday, an AI agent cost a startup $47,000 in 6 hours.
      </GradientHeading>
      <div className="mt-10 w-full max-w-2xl">
        <Timeline
          label="Simulated scenario based on documented failure patterns"
          events={[
            { time: '09:14', text: 'Agent starts processing leads, calls enrichment API ($0.05/call).', severity: 'normal' },
            { time: '09:31', text: 'Retry loop triggered. 340 calls/minute.', severity: 'warning' },
            { time: '11:45', text: 'Escalates to premium endpoint ($0.50/call).', severity: 'warning' },
            { time: '15:22', text: 'Finance sees Stripe charge: $47,312. Agent still running.', severity: 'danger' },
            { time: '15:24', text: 'Engineer kills process. No audit trail.', severity: 'danger' },
          ]}
        />
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-8 rounded-xl border border-[var(--color-error-muted)] bg-[rgba(239,68,68,0.04)] px-6 py-4 max-w-2xl">
        <div className="flex items-start gap-3">
          <Warning size={20} weight="fill" className="mt-0.5 shrink-0 text-[var(--color-error)]" />
          <p className="text-sm text-text-secondary leading-relaxed">
            No attacker. Just an agent doing its job, with <span className="text-text-primary font-medium">no budget limit, no rate cap, and no kill switch.</span>
          </p>
        </div>
      </motion.div>
    </SlideContainer>
  );
}

/* Slide 3: Why It Gets Worse */
function SlideWhyWorse() {
  return (
    <SlideContainer>
      <SectionTag>Market Context</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        The problem scales with agent adoption.
      </GradientHeading>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 max-w-3xl w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rounded-xl border border-[var(--color-error-muted)] bg-[rgba(239,68,68,0.04)] p-6">
          <div className="text-3xl font-bold text-[var(--color-error)]">85.6%</div>
          <div className="mt-2 text-sm font-medium text-text-primary">of organizations have no formal approval process for AI agent actions</div>
          <div className="mt-3 text-[10px] font-mono text-text-tertiary uppercase">Gartner, 2026</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-xl border border-[var(--color-teal-muted)] bg-[rgba(6,182,164,0.04)] p-6">
          <div className="text-3xl font-bold text-accent-teal">46% CAGR</div>
          <div className="mt-2 text-sm font-medium text-text-primary">AI Agents market: $7.8B &rarr; $52.6B by 2030</div>
          <div className="mt-3 text-[10px] font-mono text-text-tertiary uppercase">MarketsandMarkets</div>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mt-6 rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-6 max-w-3xl w-full">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-start gap-3">
            <ChartLineUp size={20} weight="bold" className="mt-0.5 shrink-0 text-accent-violet" />
            <div>
              <div className="text-sm font-medium text-text-primary">Simple math</div>
              <div className="mt-0.5 text-xs text-text-tertiary">1% unintended calls + 10x volume = 10x unintended spend</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield size={20} weight="bold" className="mt-0.5 shrink-0 text-accent-violet" />
            <div>
              <div className="text-sm font-medium text-text-primary">EU AI Act</div>
              <div className="mt-0.5 text-xs text-text-tertiary">High-risk systems require human oversight. Agents without approval workflows are non-compliant.</div>
            </div>
          </div>
        </div>
      </motion.div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-6 text-sm text-text-secondary text-center max-w-xl font-medium">
        Compliance is the forcing function. Cost is the discovery mechanism.
      </motion.p>
    </SlideContainer>
  );
}

/* Slide 4: What Palaryn Does */
function SlideWhatPalarynDoes() {
  return (
    <SlideContainer>
      <SectionTag>Solution</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        One line of config.<br />Every agent governed.
      </GradientHeading>

      {/* Pipeline diagram */}
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }} className="mt-8 w-full max-w-3xl">
        <div className="flex items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm font-mono overflow-x-auto">
          <span className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-3 py-2 text-text-secondary whitespace-nowrap">AI Agent</span>
          <span className="text-text-tertiary">&rarr;</span>
          <span className="rounded-lg border-2 border-accent-teal bg-[rgba(6,182,164,0.08)] px-4 py-2 text-accent-teal font-bold whitespace-nowrap">Palaryn</span>
          <span className="text-text-tertiary">&rarr;</span>
          <span className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-3 py-2 text-text-secondary whitespace-nowrap">External API</span>
        </div>
        <div className="mt-2 flex justify-center gap-3 text-[10px] font-mono text-text-tertiary">
          <span>Policy</span><span>|</span><span>DLP</span><span>|</span><span>Budget</span><span>|</span><span>Audit</span>
        </div>
      </motion.div>

      {/* Three concrete outcomes */}
      <div className="mt-8 grid gap-4 sm:grid-cols-3 max-w-4xl w-full">
        {[
          { icon: CurrencyDollar, text: 'Sets $50/day budget per agent. Hits limit \u2192 kills the call, alerts operator.', color: 'text-accent-teal' },
          { icon: Lock, text: 'Detects API keys and PII in outbound calls. Redacts before request leaves.', color: 'text-accent-violet' },
          { icon: CheckCircle, text: 'Requires human approval for write ops. Agent pauses \u2192 human approves \u2192 resumes.', color: 'text-[var(--color-warning)]' },
        ].map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }} className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5">
            <item.icon size={24} weight="duotone" className={item.color} />
            <p className="mt-3 text-sm text-text-secondary leading-relaxed">{item.text}</p>
          </motion.div>
        ))}
      </div>

      {/* Zero-code install */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="mt-8 w-full max-w-3xl rounded-xl border border-[var(--border)] bg-[var(--surface-0)] p-5">
        <div className="text-xs font-mono text-text-tertiary uppercase tracking-wider mb-3">Zero-code install</div>
        <div className="space-y-2 font-mono text-sm">
          <div className="rounded-lg bg-[var(--surface-1)] px-4 py-2.5 text-text-secondary">
            <span className="text-text-tertiary select-none">$ </span>
            <span className="text-accent-teal">export</span> HTTP_PROXY=http://palaryn:3128
          </div>
          <div className="rounded-lg bg-[var(--surface-1)] px-4 py-2.5 text-text-secondary">
            <span className="text-text-tertiary select-none">$ </span>
            claude mcp add --transport http palaryn https://app.palaryn.com/mcp
          </div>
        </div>
        <p className="mt-3 text-xs text-text-tertiary text-center">10 minutes from zero to governed.</p>
      </motion.div>
    </SlideContainer>
  );
}

/* Slide 5: Simulated Case Study */
function SlideCaseStudy() {
  return (
    <SlideContainer>
      <SectionTag>Case Study</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        Same scenario. With Palaryn.
      </GradientHeading>
      <div className="mt-8 w-full max-w-2xl">
        <Timeline
          events={[
            { time: '09:14', text: 'Agent starts. Palaryn logs, checks policy: ALLOW (read).', severity: 'success' },
            { time: '09:31', text: 'Retry loop. Rate limiter triggers at 100/min. Throttled. Alert sent.', severity: 'warning' },
            { time: '09:33', text: 'Premium endpoint attempt. Policy: REQUIRE_APPROVAL. Operator notified.', severity: 'warning' },
            { time: '09:35', text: 'Operator denies. Agent falls back to cache. Total spend: $12.40', severity: 'success' },
          ]}
        />
      </div>

      {/* Comparison table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8 w-full max-w-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="pb-3 text-left font-mono text-xs text-text-tertiary uppercase tracking-wider" />
              <th className="pb-3 text-center font-mono text-xs text-[var(--color-error)] uppercase tracking-wider">Without Palaryn</th>
              <th className="pb-3 text-center font-mono text-xs text-accent-teal uppercase tracking-wider">With Palaryn</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['API spend', '$47,312', '$12.40'],
              ['Time to detect', '6 hours', '2 minutes'],
              ['Audit trail', 'None', 'Full trace'],
              ['Compliance', 'Violated', 'Documented'],
            ].map(([label, without, withP]) => (
              <tr key={label} className="border-b border-[var(--border-subtle)]">
                <td className="py-3 text-text-secondary font-medium">{label}</td>
                <td className="py-3 text-center text-[var(--color-error)] font-mono">{without}</td>
                <td className="py-3 text-center text-accent-teal font-mono font-bold">{withP}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-6 text-[10px] font-mono text-text-tertiary text-center uppercase tracking-wider">
        Simulated. All pipeline steps are implemented and tested in production code.
      </motion.p>
    </SlideContainer>
  );
}

/* Slide 6: Product Depth */
function SlideProductDepth() {
  return (
    <SlideContainer>
      <SectionTag>Product</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        Not a prototype. Production-grade.
      </GradientHeading>
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-4 text-base text-text-secondary text-center max-w-xl">
        45K LOC. 3,110 tests. Live deployment.
      </motion.p>

      {/* Metrics grid */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-10 w-full max-w-4xl">
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
          {[
            { value: '3,110', label: 'Tests', sub: '82 suites' },
            { value: '1.6x', label: 'Test:code ratio', sub: 'comprehensive' },
            { value: '3', label: 'Storage backends', sub: 'Memory, Postgres, Redis' },
            { value: '2', label: 'SDKs', sub: 'TypeScript + Python' },
            { value: '4', label: 'Entry points', sub: 'API, SDK, Proxy, MCP' },
            { value: '23', label: 'Dashboard pages', sub: 'Full SaaS UI' },
            { value: '13', label: 'Pipeline steps', sub: 'Auth \u2192 Audit' },
            { value: '45K', label: 'Lines of code', sub: 'TypeScript + Python' },
          ].map((m, i) => (
            <div key={i} className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-4 text-center">
              <div className="text-2xl font-bold text-accent-teal">{m.value}</div>
              <div className="mt-1 text-xs font-medium text-text-primary">{m.label}</div>
              <div className="mt-0.5 text-[10px] text-text-tertiary">{m.sub}</div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-8 text-sm text-text-secondary text-center font-medium max-w-lg">
        Production-ready. Built to scale with a team.
      </motion.p>
    </SlideContainer>
  );
}

/* Slide 7: Market + Acquisitions */
function SlideMarketAcquisitions() {
  return (
    <SlideContainer>
      <SectionTag>Market</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        $2B+ in AI security M&amp;A.<br />And the market is just starting.
      </GradientHeading>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-10 max-w-3xl w-full overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="pb-4 font-mono text-sm text-text-tertiary uppercase tracking-wider">Company</th>
              <th className="pb-4 font-mono text-sm text-text-tertiary uppercase tracking-wider">Acquirer</th>
              <th className="pb-4 font-mono text-sm text-text-tertiary uppercase tracking-wider">Value</th>
              <th className="pb-4 font-mono text-sm text-text-tertiary uppercase tracking-wider">Year</th>
            </tr>
          </thead>
          <tbody>
            {ACQUISITIONS.map((a, i) => (
              <motion.tr key={a.company} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.08 }} className="border-b border-[var(--border-subtle)]">
                <td className="py-3.5 font-medium text-text-primary text-sm">{a.company}</td>
                <td className="py-3.5 text-text-secondary text-sm">{a.acquirer}</td>
                <td className="py-3.5 font-mono text-accent-teal text-sm font-medium">{a.value}</td>
                <td className="py-3.5 text-text-tertiary text-sm">{a.year}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-6 text-sm text-text-tertiary text-center font-medium">
        Every company in the table was 2&ndash;3 years old. We are at month 4.
      </motion.p>
    </SlideContainer>
  );
}

/* Slide 8: Why Palaryn Wins */
function SlideWhyWins() {
  return (
    <SlideContainer>
      <SectionTag>Moat</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        Zero-code install is the wedge.<br />Data is the moat.
      </GradientHeading>

      <div className="mt-10 grid gap-8 lg:grid-cols-2 max-w-5xl w-full items-start">
        {/* Adoption flywheel */}
        <Flywheel steps={[
          { label: 'Zero-code install', detail: 'HTTP_PROXY / MCP \u2192 10-min onboarding' },
          { label: 'Immediate value', detail: 'Cost visibility + policy enforcement from day 1' },
          { label: 'Data accumulation', detail: 'Audit trail enterprises can\'t abandon' },
          { label: 'Policy lock-in', detail: 'YAML/OPA rules encode institutional knowledge' },
          { label: 'Compliance dependency', detail: 'Logs become compliance artifacts' },
        ]} />

        {/* Strategic positioning */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-4">
          <motion.div variants={fadeUp} className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5">
            <div className="text-xs font-mono font-bold text-accent-teal uppercase tracking-wider mb-3">Strategic position</div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Lock size={16} weight="bold" className="mt-0.5 shrink-0 text-text-tertiary" />
                <p className="text-xs text-text-secondary">Acquired players are now vendor-locked in CrowdStrike / SentinelOne stacks.</p>
              </div>
              <div className="flex items-start gap-3">
                <ArrowsClockwise size={16} weight="bold" className="mt-0.5 shrink-0 text-text-tertiary" />
                <p className="text-xs text-text-secondary">Platform vendors will only govern their own models. We govern all of them.</p>
              </div>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} className="rounded-xl border border-[var(--color-warning-muted)] bg-[rgba(234,179,8,0.04)] p-5">
            <div className="text-xs font-mono font-bold text-[var(--color-warning)] uppercase tracking-wider mb-2">Honest gap</div>
            <p className="text-xs text-text-secondary">
              We don&apos;t do prompt injection detection. That&apos;s model-input. We govern tool execution (model-output). Complementary, not competitive.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </SlideContainer>
  );
}

/* Slide 9: Traction + Milestones */
function SlideTractionMilestones() {
  return (
    <SlideContainer>
      <SectionTag>Traction</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        Tech risk eliminated.<br />Commercial risk is what your money solves.
      </GradientHeading>

      {/* Done / Not done */}
      <div className="mt-10 grid gap-6 sm:grid-cols-2 max-w-3xl w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rounded-xl border border-[var(--color-teal-muted)] bg-[rgba(6,182,164,0.04)] p-6">
          <div className="text-xs font-mono font-bold text-accent-teal uppercase tracking-wider mb-4">Done (risk eliminated)</div>
          <div className="space-y-2.5">
            {[
              'Complete 13-step pipeline',
              '3,110 tests, zero tech debt',
              'Live at app.palaryn.com with MCP OAuth',
              'Stripe billing (Pro + Business plans)',
              'TypeScript + Python SDKs',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-xs text-text-secondary">
                <CheckCircle size={14} weight="fill" className="shrink-0 text-accent-teal" />
                {item}
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-6">
          <div className="text-xs font-mono font-bold text-text-tertiary uppercase tracking-wider mb-4">Not done (the plan)</div>
          <div className="space-y-2.5">
            {[
              '0 paying customers (honest)',
              'No SOC 2 (requires funding)',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-xs text-text-secondary">
                <Clock size={14} weight="bold" className="shrink-0 text-text-tertiary" />
                {item}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* 12-month milestones */}
      <div className="mt-8 w-full">
        <MilestoneBar phases={[
          { label: 'Months 1\u20133', items: ['5 design partners', '2 hires (GTM + engineer)', 'First paying customer'] },
          { label: 'Months 4\u20136', items: ['First 3 paying customers', 'SOC 2 Type I started'] },
          { label: 'Months 7\u201312', items: ['$100K ARR target', '10 paying customers'] },
        ]} />
      </div>
    </SlideContainer>
  );
}

/* Slide 10: Founder */
function SlideFounder() {
  return (
    <SlideContainer>
      <SectionTag>Founder</SectionTag>
      <GradientHeading className="text-center max-w-3xl">
        The person building this.
      </GradientHeading>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-10 rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-8 max-w-lg w-full">
        <div className="flex flex-col items-center gap-4">
          <img src="/patryk.jpg" alt="Patryk Juniszewski" className="h-28 w-28 shrink-0 rounded-full object-cover ring-2 ring-accent-violet/30" />
          <div className="text-center">
            <div className="text-xl font-semibold text-text-primary">Patryk Juniszewski</div>
            <div className="text-sm text-text-tertiary">Founder</div>
          </div>
        </div>
        <div className="mt-5 space-y-1.5 text-sm text-text-secondary">
          <div className="flex items-center gap-2">
            <span className="text-accent-teal font-mono text-xs font-bold">10 yrs</span>
            <span>software engineering, full-stack + infrastructure</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-accent-teal font-mono text-xs font-bold">&nbsp;2 yrs</span>
            <span>AI systems, agent tooling</span>
          </div>
        </div>
        <div className="mt-4 text-sm text-text-secondary">
          Former Software Manager at <span className="text-text-primary font-medium">Mudita</span>.
        </div>
        <div className="mt-3 rounded-lg border border-[var(--color-teal-muted)] bg-[rgba(6,182,164,0.04)] px-4 py-3 text-sm text-text-secondary">
          Built Palaryn end-to-end: <span className="text-accent-teal font-mono font-medium">45K LOC, 3,110 tests</span>.
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-6 rounded-xl border border-[var(--color-violet-muted)] bg-[rgba(139,92,246,0.04)] px-6 py-5 max-w-lg w-full text-center">
        <div className="text-base font-semibold text-accent-violet mb-2">Open to collaboration</div>
        <p className="text-sm text-text-secondary">
          Looking for a <span className="text-text-primary font-medium">technical co-founder</span> or <span className="text-text-primary font-medium">early team members</span> passionate about AI infrastructure, developer tools, or enterprise security.
        </p>
        <p className="mt-2 text-xs text-text-tertiary">
          If this resonates — let's talk.
        </p>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-6 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <EnvelopeSimple size={16} weight="bold" className="text-accent-teal" />
          <span className="font-mono text-accent-teal">patryk.juniszewski@palaryn.com</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Rocket size={16} weight="bold" className="text-accent-teal" />
          <span className="font-mono text-accent-teal">app.palaryn.com</span>
        </div>
      </motion.div>
    </SlideContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Slides Array                                                       */
/* ------------------------------------------------------------------ */

const SLIDES: Array<{ component: React.FC; title: string }> = [
  { component: SlideCover, title: 'Cover' },
  { component: SlideHorror, title: 'Horror Scenario' },
  { component: SlideWhyWorse, title: 'Why It Gets Worse' },
  { component: SlideWhatPalarynDoes, title: 'What Palaryn Does' },
  { component: SlideCaseStudy, title: 'Case Study' },
  { component: SlideProductDepth, title: 'Product Depth' },
  { component: SlideMarketAcquisitions, title: 'Market' },
  { component: SlideWhyWins, title: 'Why We Win' },
  { component: SlideTractionMilestones, title: 'Traction' },
  { component: SlideFounder, title: 'Founder' },
];

/* ------------------------------------------------------------------ */
/*  Main Pitch Component                                               */
/* ------------------------------------------------------------------ */

export function Pitch() {
  const navigate = useNavigate();
  const [[currentSlide, direction], setSlideState] = useState([0, 0]);

  // Parse hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const num = parseInt(hash.slice(1), 10);
      if (!isNaN(num) && num >= 0 && num < TOTAL_SLIDES) {
        setSlideState([num, 0]);
      }
    }
  }, []);

  // Update hash on slide change
  useEffect(() => {
    window.location.hash = `#${currentSlide}`;
  }, [currentSlide]);

  const goTo = useCallback((slide: number, dir?: number) => {
    if (slide < 0 || slide >= TOTAL_SLIDES) return;
    setSlideState([slide, dir ?? (slide > currentSlide ? 1 : -1)]);
  }, [currentSlide]);

  const next = useCallback(() => goTo(currentSlide + 1, 1), [currentSlide, goTo]);
  const prev = useCallback(() => goTo(currentSlide - 1, -1), [currentSlide, goTo]);

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        navigate('/');
      } else if (e.key === 'Home') {
        e.preventDefault();
        goTo(0, -1);
      } else if (e.key === 'End') {
        e.preventDefault();
        goTo(TOTAL_SLIDES - 1, 1);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [next, prev, navigate, goTo]);

  // Touch/swipe support
  const touchStartX = useRef(0);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
  }, [next, prev]);

  const SlideComponent = SLIDES[currentSlide].component;

  return (
    <div
      className="fixed inset-0 bg-surface-0 overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background glow */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(6, 182, 164, 0.06) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 60% 10%, rgba(139, 92, 246, 0.04) 0%, transparent 60%)',
        }}
      />

      {/* Slide content */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="absolute inset-0 overflow-y-auto"
        >
          <SlideComponent />
        </motion.div>
      </AnimatePresence>

      {/* Navigation arrows */}
      {currentSlide > 0 && (
        <button
          onClick={prev}
          className="fixed left-4 top-1/2 -translate-y-1/2 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-1)] text-text-secondary transition-all hover:border-[var(--text-tertiary)] hover:text-text-primary sm:left-6 sm:h-12 sm:w-12"
          aria-label="Previous slide"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
      )}
      {currentSlide < TOTAL_SLIDES - 1 && (
        <button
          onClick={next}
          className="fixed right-4 top-1/2 -translate-y-1/2 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-1)] text-text-secondary transition-all hover:border-[var(--text-tertiary)] hover:text-text-primary sm:right-6 sm:h-12 sm:w-12"
          aria-label="Next slide"
        >
          <ArrowRight size={20} weight="bold" />
        </button>
      )}

      {/* Progress dots */}
      <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2">
        {SLIDES.map((slide, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="group relative flex items-center justify-center p-1"
            aria-label={`Go to slide ${i + 1}: ${slide.title}`}
          >
            <span
              className={`block rounded-full transition-all duration-200 ${
                i === currentSlide
                  ? 'h-2.5 w-2.5'
                  : 'h-1.5 w-1.5 opacity-40 group-hover:opacity-70'
              }`}
              style={{
                background: i === currentSlide ? 'var(--gradient-accent)' : 'var(--text-tertiary)',
              }}
            />
            {/* Tooltip on hover */}
            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-[var(--surface-3)] px-2 py-1 text-[10px] text-text-secondary opacity-0 transition-opacity group-hover:opacity-100">
              {slide.title}
            </span>
          </button>
        ))}
      </div>

      {/* Slide counter */}
      <div className="fixed bottom-6 right-6 z-50 text-xs font-mono text-text-tertiary">
        {currentSlide + 1} / {TOTAL_SLIDES}
      </div>

      {/* Escape hint */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-3 py-1.5 text-xs text-text-tertiary transition-all hover:text-text-secondary"
        >
          <kbd className="rounded bg-[var(--surface-0)] px-1.5 py-0.5 font-mono text-[10px]">ESC</kbd>
          Back
        </button>
      </div>
    </div>
  );
}
